import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Upload, X, Loader2 } from 'lucide-react';
import { getImageUrl } from '../utils/api';

const API = process.env.REACT_APP_BACKEND_URL;

const FileUpload = ({ onFileUploaded, currentFile, folder = 'gallery' }) => {
  const [preview, setPreview] = useState(currentFile || '');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPEG, PNG, GIF, or WebP images.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await fetch(`${API}/api/admin/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Wait 5 seconds for R2 to propagate the image before updating preview
        setUploading(false);
        setProcessing(true);
        await new Promise(resolve => setTimeout(resolve, 5000));
        setProcessing(false);
        setPreview(data.url);
        onFileUploaded(data.url);
      } else {
        setError(data.detail || 'Failed to upload image');
        setUploading(false);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image. Please try again.');
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onFileUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="file-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />
      
      {preview ? (
        <div className="file-upload-preview relative">
          <img src={getImageUrl(preview)} alt="Preview" className="upload-preview-image rounded-lg max-h-48 object-cover" />
          <button 
            onClick={handleRemove} 
            className="file-upload-remove absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div 
          onClick={() => !uploading && !processing && fileInputRef.current?.click()}
          className={`flex flex-col items-center gap-3 p-6 border-2 border-dashed border-[#3d4a35] rounded-lg bg-[#232921] cursor-pointer hover:bg-[#2d3728] transition-colors ${(uploading || processing) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {uploading ? (
            <>
              <Loader2 size={32} className="text-[#8b9a7e] animate-spin" />
              <span className="text-sm text-[#8b9a7e]">Uploading...</span>
            </>
          ) : processing ? (
            <>
              <Loader2 size={32} className="text-[#8b9a7e] animate-spin" />
              <span className="text-sm text-[#8b9a7e]">Processing image...</span>
              <span className="text-xs text-[#6b7a5e]">Waiting for CDN propagation</span>
            </>
          ) : (
            <>
              <Upload size={32} className="text-[#8b9a7e]" />
              <span className="text-sm text-[#8b9a7e]">Click to upload image</span>
              <span className="text-xs text-[#6b7a5e]">JPEG, PNG, GIF, WebP (max 10MB)</span>
            </>
          )}
        </div>
      )}
      
      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;